/**
 * Sleep guide article helpers. Words come from the page (section settings); nothing typed here.
 *   <sunday-article-body>  wraps tables so they fit a phone, gives headings ids and fills
 *                          "In this article" (3+ headings; the folded box and the desktop rail),
 *                          folds the questions under the headings named in data-faq, turns YouTube
 *                          iframes into a still + play button that loads the video only on tap.
 *   <sunday-share>         "Copy link".
 */
class SundayArticleBody extends HTMLElement {
  connectedCallback() {
    // tables: one scroll-free wrapper each
    for (const t of this.querySelectorAll('table')) {
      if (t.parentElement.classList.contains('sd-btw')) continue;
      const w = document.createElement('div');
      w.className = 'sd-btw';
      t.replaceWith(w);
      w.append(t);
    }
    // headings → contents (the folded box on a phone, the rail on a wide screen)
    const heads = [...this.querySelectorAll('h1, h2')].filter((h) => h.textContent.trim());
    heads.forEach((h, i) => { if (!h.id) h.id = `sd-bs${i}`; });
    const root = this.closest('.sd-bart') || this.parentElement;
    if (heads.length > 2) {
      const items = () => heads.map((h) => {
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.href = `#${h.id}`;
        a.textContent = h.textContent.trim();
        li.append(a);
        return li;
      });
      const toc = root.querySelector('.sd-btoc');
      if (toc) {
        toc.querySelector('em').textContent = String(heads.length);
        toc.querySelector('ol').replaceChildren(...items());
        toc.hidden = false;
      }
      const rail = root.querySelector('.sd-brtoc');
      if (rail) {
        rail.querySelector('ol').replaceChildren(...items());
        rail.hidden = false;
      }
    }
    // questions under a "Frequently asked questions" heading fold: <p><strong>Q</strong><br>A</p>
    const faqNames = (this.dataset.faq || '').split(',').map((x) => x.trim().toLowerCase()).filter(Boolean);
    for (const h of heads) {
      if (!faqNames.includes(h.textContent.trim().toLowerCase())) continue;
      const box = document.createElement('div');
      box.className = 'sd-bfaq';
      let n = h.nextElementSibling;
      while (n && !/^H[1-3]$/.test(n.tagName)) {
        const next = n.nextElementSibling;
        const q = n.tagName === 'P' && n.firstElementChild && /^(STRONG|B)$/.test(n.firstElementChild.tagName) ? n.firstElementChild : null;
        const br = q && q.nextElementSibling && q.nextElementSibling.tagName === 'BR' ? q.nextElementSibling : null;
        if (q && br && q.textContent.trim()) {
          const d = document.createElement('details');
          const sm = document.createElement('summary');
          sm.textContent = q.textContent.trim();
          const ans = document.createElement('p');
          q.remove();
          br.remove();
          ans.append(...n.childNodes);
          d.append(sm, ans);
          box.append(d);
          n.remove();
        } else if (box.childElementCount) {
          break;
        }
        n = next;
      }
      if (box.childElementCount) h.after(box);
    }
    // YouTube: a still and a play button until tapped
    for (const f of this.querySelectorAll('iframe[src*="youtube.com/embed/"], iframe[src*="youtube-nocookie.com/embed/"]')) {
      const id = (f.src.match(/embed\/([\w-]{6,})/) || [])[1];
      if (!id) continue;
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'sd-bvid';
      b.setAttribute('aria-label', this.dataset.play || '');
      b.innerHTML = `<img src="https://i.ytimg.com/vi/${id}/hqdefault.jpg" alt="" loading="lazy" width="480" height="360"><span aria-hidden="true"></span>`;
      b.addEventListener('click', () => {
        const n = document.createElement('iframe');
        // The store sends Referrer-Policy: same-origin; YouTube refuses to play without a referrer (error 153).
        n.referrerPolicy = 'strict-origin-when-cross-origin';
        n.src = `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0`;
        n.allow = 'autoplay; encrypted-media; picture-in-picture';
        n.allowFullscreen = true;
        n.className = 'sd-bvidf';
        b.replaceWith(n);
      });
      f.replaceWith(b);
    }
  }
}

class SundayShare extends HTMLElement {
  connectedCallback() {
    const b = this.querySelector('[data-copy]');
    if (!b) return;
    b.addEventListener('click', async () => {
      try {
        await navigator.clipboard.writeText(b.dataset.copy);
        b.textContent = this.dataset.copied || b.textContent;
      } catch (e) {
        /* clipboard blocked: leave the button as it is */
      }
    });
  }
}

if (!customElements.get('sunday-article-body')) customElements.define('sunday-article-body', SundayArticleBody);
if (!customElements.get('sunday-share')) customElements.define('sunday-share', SundayShare);
